// ENG-6035: restart trigger for Slack behaviour-field changes.
//
// The webapp's Slack panel saves allowed_users / thread_auto_follow /
// channel_response_mode via the agent-edit channel_upserts flow. The
// manager's channel-cred hash correctly invalidates on the change and the
// adapter rewrites the slack-channel entry's env in .mcp.json — but the
// running slack-channel MCP child read SLACK_ALLOWED_USERS (and friends)
// from process.env exactly once at boot, so the on-disk rewrite never
// reaches it. Same last-hop gap ENG-5858 closed for sender_policy and
// ENG-6024 closed for Teams behaviour fields: the channel-set restart only
// fires on membership change, and the .mcp.json drift watcher deliberately
// suppresses value-only restarts (ENG-5537, Composio URL-rotation flapping).
//
// This trigger hashes ONLY the operator-editable behaviour subset below, so
// credential/token churn in the wider channel config can't fire it — and the
// subset itself only changes on an explicit operator save, so it can't flap.
//
// Closing this for Slack also retires the documented Slack half of the
// ENG-5537 behaviour-restart gap (thread_auto_follow / channel_response_mode
// previously required a manual session restart to take effect).
//
// ENG-6065: Block Kit flags (`block_kit_enabled` / `block_kit_ask_user_enabled`)
// were INCLUDED here so an operator's mid-session toggle took effect.
// ENG-6504 RETIRED them: Block Kit + ask_user are now permanently on for every
// Slack channel (the adapter hardwires the env to 'true'), so there is no
// operator toggle and their env output is constant — nothing for this trigger
// to track.
//
// ENG-6464: `ack_reaction` and `skip_reaction` are now INCLUDED. They were
// excluded while ack_reaction had no fleet consumer (the slack-channel MCP
// hardcoded 'eyes'); ENG-6464 made them real env consumers
// (SLACK_ACK_REACTION / SLACK_SKIP_REACTION, read once at MCP boot to render
// the ack emoji + the seen-but-skipping instruction). So an operator changing
// either emoji must restart the session to take effect — the same gap the
// trigger closes for every other behaviour field. The decision's first-poll
// baseline means adding these fields does NOT restart on deploy; only a later
// operator change does.

/**
 * The Slack behaviour fields that map to slack-channel MCP boot-time env
 * vars (claudecode adapter, persistent slack entry). Values are normalised
 * to the adapter's own absent-field defaults so an explicit save of the
 * default value hashes identically to the field being absent — the env
 * output is the same, so no restart is warranted.
 */
export interface SlackBehaviourSubset {
  thread_auto_follow: string;
  channel_response_mode: string;
  /** ENG-6464: ack/skip reaction emoji shortcodes. The adapter emits
   *  SLACK_ACK_REACTION / SLACK_SKIP_REACTION only when non-empty; normalise
   *  absent → '' so a config without them hashes identically to one with them
   *  explicitly cleared (both ⇒ no env var). */
  ack_reaction: string;
  skip_reaction: string;
  /** Normalised: trimmed non-empty strings only, original order preserved
   *  (the adapter join is order-sensitive; reordering IS an env change but
   *  the webapp always writes a de-duped parse of the input, so an
   *  unchanged save round-trips identically). Empty/absent → []. */
  allowed_users: string[];
  // ENG-6504: block_kit_enabled / block_kit_ask_user_enabled were removed from
  // the subset — the adapter now hardwires SLACK_BLOCK_KIT_ENABLED /
  // SLACK_BLOCK_KIT_ASK_USER_ENABLED to 'true' for every Slack channel, so
  // their env output is constant and can never move the hash.
}

export function extractSlackBehaviourSubset(
  config: Record<string, unknown> | null | undefined,
): SlackBehaviourSubset {
  const rawAllowed = config?.['allowed_users'];
  return {
    thread_auto_follow:
      (config?.['thread_auto_follow'] as string | undefined) ?? 'off',
    channel_response_mode:
      (config?.['channel_response_mode'] as string | undefined) ?? 'mention_only',
    // ENG-6464: trimmed string, absent → '' (matches the adapter's
    // emit-only-when-truthy behaviour, so unset and "" hash identically).
    ack_reaction: ((config?.['ack_reaction'] as string | undefined) ?? '').trim(),
    skip_reaction: ((config?.['skip_reaction'] as string | undefined) ?? '').trim(),
    // Mirror the adapter's defensive filter exactly — a malformed entry the
    // adapter would drop must not count as an env change.
    allowed_users: Array.isArray(rawAllowed)
      ? rawAllowed
          .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
          .map((v) => v.trim())
      : [],
  };
}

// The restart decision itself (first-poll baseline, no-change debounce,
// persistent/claude-code/session-healthy gates, same-tick restart
// suppression) is identical to the sender-policy trigger — reuse it under a
// call-site-accurate name rather than duplicating the tree.
export { decideSenderPolicyRestart as decideSlackBehaviourRestart } from './sender-policy-restart-decision.js';
